import { z } from 'zod';

// src/types/employee.ts
var EMPLOYEE_STATUSES = [
  { value: "probation", label: "Probation" },
  { value: "terminated", label: "Terminated" }
];
var EMPLOYMENT_TYPES = [];

// src/types/project.ts
var PROJECT_ACTIVITY_TYPES = [
  "task.created",
  "task.status_changed",
  "task.assigned",
  "task.commented",
  "time.logged",
  "member.added",
  "member.removed",
  "project.updated"
];
var PROJECT_COLORS = [
  "#5d6d7e",
  // slate
  "#6b8e7b",
  // sage
  "#7b6d8e",
  // lavender
  "#8e7b5d",
  // ochre
  "#7b5d6d",
  // dusty rose
  "#5d7e7b",
  // teal
  "#7e5d5d",
  // terracotta
  "#8e6d5d"
  // warm brown
];

// src/types/board.ts
var BOARD_MEMBER_ROLES = [
  // What each role may do is the BOARD's to decide — these describe the presets
  // a board starts with, and a board that has been customized may contradict
  // every one of them. The role picker should say so.
  { value: "owner", label: "Owner", description: "By default everything, and always the one who hands the board on" },
  { value: "admin", label: "Admin", description: "By default the board's shape: sprints, columns, tags, settings, automations, members" },
  { value: "member", label: "Member", description: "By default creating and editing tasks, commenting, moving cards" },
  { value: "viewer", label: "Viewer", description: "By default reading the board and following tasks" }
];
var BOARD_TEMPLATES = [
  {
    key: "sprint",
    name: "Sprint Board",
    description: "Agile sprint workflow",
    statuses: [
      { name: "Backlog", category: "backlog", color: "#94A3B8" },
      { name: "To Do", category: "backlog", color: "#64748B" },
      { name: "In Progress", category: "active", color: "#3B82F6" },
      { name: "In Review", category: "active", color: "#F59E0B" },
      { name: "Done", category: "done", color: "#10B981" }
    ]
  },
  {
    key: "recruitment",
    name: "Recruitment Pipeline",
    description: "Hiring workflow for recruiters",
    statuses: [
      { name: "Applied", category: "backlog", color: "#94A3B8" },
      { name: "Screening", category: "active", color: "#8B5CF6" },
      { name: "Interview", category: "active", color: "#3B82F6" },
      { name: "Technical", category: "active", color: "#06B6D4" },
      { name: "Offer", category: "active", color: "#F59E0B" },
      { name: "Hired", category: "done", color: "#10B981" },
      { name: "Rejected", category: "cancelled", color: "#EF4444" }
    ]
  },
  {
    key: "sales",
    name: "Sales Pipeline",
    description: "Sales process tracking",
    statuses: [
      { name: "Lead", category: "backlog", color: "#94A3B8" },
      { name: "Contacted", category: "active", color: "#8B5CF6" },
      { name: "Proposal", category: "active", color: "#3B82F6" },
      { name: "Negotiation", category: "active", color: "#F59E0B" },
      { name: "Won", category: "done", color: "#10B981" },
      { name: "Lost", category: "cancelled", color: "#EF4444" }
    ]
  },
  {
    key: "bug-tracking",
    name: "Bug Tracking",
    description: "Issue tracking workflow",
    statuses: [
      { name: "Reported", category: "backlog", color: "#94A3B8" },
      { name: "Confirmed", category: "backlog", color: "#64748B" },
      { name: "In Progress", category: "active", color: "#3B82F6" },
      { name: "Fixed", category: "active", color: "#F59E0B" },
      { name: "Verified", category: "done", color: "#10B981" }
    ]
  },
  {
    key: "kanban",
    name: "Kanban",
    description: "Simple kanban workflow",
    statuses: [
      { name: "Backlog", category: "backlog", color: "#94A3B8" },
      { name: "Ready", category: "backlog", color: "#64748B" },
      { name: "In Progress", category: "active", color: "#3B82F6" },
      { name: "Done", category: "done", color: "#10B981" }
    ]
  },
  {
    key: "custom",
    name: "Custom",
    description: "Start with a minimal board",
    statuses: [
      { name: "To Do", category: "backlog", color: "#94A3B8" },
      { name: "Done", category: "done", color: "#10B981" }
    ]
  }
];
var DEPARTMENT_TEMPLATE_MAP = [
  { keywords: ["sales", "business development", "revenue", "commercial"], template: "sales" },
  { keywords: ["recruit", "hiring", "talent", "people", "hr", "human resource"], template: "recruitment" },
  { keywords: ["engineering", "development", "dev", "tech", "software", "product"], template: "sprint" },
  { keywords: ["qa", "quality", "testing", "test"], template: "bug-tracking" },
  { keywords: ["design", "ux", "ui", "creative"], template: "kanban" },
  { keywords: ["marketing", "content", "growth"], template: "kanban" },
  { keywords: ["support", "customer success", "service"], template: "kanban" }
];
function suggestTemplateForDepartment(departmentName) {
  const lower = departmentName.toLowerCase();
  for (const mapping of DEPARTMENT_TEMPLATE_MAP) {
    if (mapping.keywords.some((kw) => lower.includes(kw))) {
      return mapping.template;
    }
  }
  return "kanban";
}

// src/types/board-permissions.ts
var BOARD_ROLE_LEVEL = /* @__PURE__ */ new Map([
  ["owner", 40],
  ["admin", 30],
  ["member", 20],
  ["viewer", 10]
]);
var BOARD_ROLE_ORDER = [
  "owner",
  "admin",
  "member",
  "viewer"
];
function boardRoleLevel(role) {
  return role ? BOARD_ROLE_LEVEL.get(role) ?? 0 : 0;
}
function isBoardRole(role) {
  return BOARD_ROLE_LEVEL.has(role);
}
function grantableBoardRoles(role) {
  const cap = boardRoleLevel(role);
  if (cap === 0) return [];
  return BOARD_ROLE_ORDER.filter((r) => boardRoleLevel(r) <= cap);
}
var BOARD_PERMISSIONS = [
  {
    value: "task.manage",
    group: "Tasks and comments",
    label: "Create and edit tasks",
    description: "Any field \u2014 title, description, status, assignee, priority, estimate, dates, sprint, parent, links, tags \u2014 plus dragging, attachments, and putting a task into a sprint."
  },
  {
    value: "task.bulk",
    group: "Tasks and comments",
    label: "Bulk actions",
    description: 'Move several tasks between statuses, assign them, or move them to another board \u2014 the same edits "Create and edit tasks" covers, applied to many at once, so a role given this and not that can still restatus and reassign. The target board asks for its own permission, and deleting in bulk asks per task like deleting one.'
  },
  {
    value: "task.delete.own",
    group: "Tasks and comments",
    label: "Delete own tasks",
    description: 'Delete tasks this person reported, singly or in bulk. A task nobody is recorded as reporting is not "own".'
  },
  {
    value: "task.delete.any",
    group: "Tasks and comments",
    label: "Delete any task",
    description: "Delete a task whoever reported it, including this person's own, singly or in bulk."
  },
  {
    value: "comment.write",
    group: "Tasks and comments",
    label: "Comment",
    description: "Write comments, and edit or delete their own."
  },
  {
    value: "overview.publish",
    group: "Tasks and comments",
    label: "Publish an Update",
    description: "Post an Update on the board Overview."
  },
  {
    value: "sprint.manage",
    group: "Board setup",
    label: "Sprints",
    description: "Create, edit, reorder, start, complete, duplicate and delete sprints. Putting one\u2019s own task into a sprint is task editing, not this."
  },
  {
    value: "column.manage",
    group: "Board setup",
    label: "Columns",
    description: "Create, rename, reorder and delete the board\u2019s statuses."
  },
  {
    value: "tag.manage",
    group: "Board setup",
    label: "Tags",
    description: "Create, rename, recolour and delete the board\u2019s tags."
  },
  {
    value: "board.settings",
    group: "Board setup",
    label: "Board properties and settings",
    description: "Name, description, colour, icon, status, priority, target date and the board lead; default status and assignee, estimate unit, auto-archive, the display toggles and whether anyone in the organisation can be assigned. Visibility is not included: it has a row of its own."
  },
  {
    value: "automation.manage",
    group: "Board setup",
    label: "Automations",
    description: "Create, edit, delete, enable, test and generate automation rules."
  },
  {
    value: "member.manage",
    group: "Board setup",
    label: "Members",
    description: "Add and remove members, and set which role each of them holds. Always on for the Owner, who would otherwise be unable to hand the board over."
  },
  {
    value: "visibility.manage",
    group: "Ownership",
    label: "Switch visibility",
    description: "Public \u2194 Private. Enough on its own \u2014 it does not also need board properties."
  },
  {
    value: "board.delete",
    group: "Ownership",
    label: "Delete the board",
    description: "Not given to an Admin by default."
  }
];
var BOARD_PERMISSION_VALUES = BOARD_PERMISSIONS.map((p) => p.value);
var BOARD_ROLE_PRESETS = {
  viewer: [],
  member: ["task.manage", "task.bulk", "comment.write", "overview.publish"],
  admin: [
    "task.manage",
    "task.bulk",
    "task.delete.own",
    "task.delete.any",
    "comment.write",
    "overview.publish",
    "sprint.manage",
    "column.manage",
    "tag.manage",
    "board.settings",
    "automation.manage",
    "member.manage"
  ],
  owner: BOARD_PERMISSION_VALUES
};
var OWNER_LOCKED_PERMISSIONS = ["member.manage"];
function boardPermissionsFor(role, matrix) {
  const stored = matrix?.[role];
  const set = Array.isArray(stored) ? stored : BOARD_ROLE_PRESETS[role];
  const granted = new Set(set.filter((p) => BOARD_PERMISSION_VALUES.includes(p)));
  if (role === "owner") for (const p of OWNER_LOCKED_PERMISSIONS) granted.add(p);
  return BOARD_PERMISSION_VALUES.filter((p) => granted.has(p));
}

// src/types/time-tracker.ts
var TIME_ENTRY_SOURCES = ["manual", "timer", "clockify", "tempo", "paper"];
var USER_WRITABLE_TIME_ENTRY_SOURCES = ["manual", "timer"];

// src/types/employee-profile-block.ts
var EMPLOYEE_PROFILE_SECTIONS = [
  "JOB",
  "EMERGENCY",
  "DOCUMENTS",
  "ASSETS",
  "ACCESS",
  "NOTES",
  "ABSENCES",
  "COMPENSATIONS"
];

// src/types/employee-profile-block-field.ts
var EMPLOYEE_PROFILE_FIELD_TYPES = [
  "SHORT_TEXT",
  "LONG_TEXT",
  "EMAIL",
  "PHONE",
  "URL",
  "NUMBER",
  "DATE",
  "DROPDOWN",
  "CHECKBOX",
  "FILE"
];

// src/types/employee-profile-field-mapping.ts
var EMPLOYEE_PROFILE_SECTION_LABELS = {
  JOB: "Job",
  EMERGENCY: "Emergency",
  DOCUMENTS: "Documents",
  ASSETS: "Assets",
  ACCESS: "Access",
  NOTES: "Notes",
  ABSENCES: "Absences",
  COMPENSATIONS: "Compensations"
};
var MAPPING_KEY_SYSTEM_PREFIX = "system";
var MAPPING_KEY_CUSTOM_PREFIX = "custom";
function buildSystemFieldMappingKey(systemKey) {
  return `${MAPPING_KEY_SYSTEM_PREFIX}:${systemKey}`;
}
function buildCustomFieldMappingKey(fieldId) {
  return `${MAPPING_KEY_CUSTOM_PREFIX}:${fieldId}`;
}
function parseMappingKey(key) {
  const separator = key.indexOf(":");
  if (separator === -1) return null;
  const prefix = key.slice(0, separator);
  const rest = key.slice(separator + 1);
  if (!rest) return null;
  if (prefix === MAPPING_KEY_SYSTEM_PREFIX) return { source: "SYSTEM", systemKey: rest };
  if (prefix === MAPPING_KEY_CUSTOM_PREFIX) return { source: "CUSTOM", fieldId: rest };
  return null;
}
function formatMappableFieldLabel(field) {
  if (field.breadcrumb.length === 0) return field.label;
  return `${field.label} (${field.breadcrumb.join(" \u2192 ")})`;
}

// src/types/preboarding-form.ts
var PREBOARDING_FORM_TYPES = ["PREBOARDING"];
var PREBOARDING_FORM_STATUSES = ["DRAFT", "PUBLISHED", "ARCHIVED"];
var PREBOARDING_FORM_FIELD_KINDS = ["MAPPED", "DOCUMENT"];
var DEFAULT_PREBOARDING_DOCUMENT_FOLDER = "Pre-boarding";

// src/types/preboarding-form-variables.ts
var PREBOARDING_FORM_VARIABLES = [
  { token: "employeeFirstName", label: "First name", group: "Employee" },
  { token: "employeeLastName", label: "Last name", group: "Employee" },
  { token: "employeeFullName", label: "Full name", group: "Employee" },
  { token: "position", label: "Position", group: "Employee" },
  { token: "department", label: "Department", group: "Employee" },
  { token: "startDate", label: "Start date", group: "Employee" },
  { token: "companyName", label: "Company name", group: "Company" }
];
function formatVariableToken(token) {
  return `{{${token}}}`;
}

// src/types/preboarding-submission.ts
var PREBOARDING_CONFLICT_STATUSES = ["PENDING", "RESOLVED"];
var PREBOARDING_CONFLICT_RESOLUTIONS = ["OVERWRITE", "KEEP"];
var uuidSchema = z.string().uuid();
var paginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20)
});
var sortOrderSchema = z.enum(["asc", "desc"]);
var sortSchema = z.object({
  field: z.string(),
  order: sortOrderSchema
});
var dateStringSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
  message: "Date must be in YYYY-MM-DD format"
});
var emailSchema = z.string().email();
var phoneSchema = z.string().regex(/^\+?[\d\s-()]+$/, {
  message: "Invalid phone number format"
}).optional();
var userRoleSchema = z.enum([
  "super_admin",
  "hr_admin",
  "hr_manager",
  "manager",
  "employee"
]);
var googleAuthSchema = z.object({
  code: z.string().min(1, "Authorization code is required"),
  redirectUri: z.string().url("Invalid redirect URI")
});
var refreshTokenSchema = z.object({
  refreshToken: z.string().min(1, "Refresh token is required")
});
var employmentStatusSchema = z.enum(["active", "inactive", "on_leave", "terminated"]);
var employmentTypeSchema = z.enum(["full_time", "part_time", "contractor", "intern"]);
var createEmployeeSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(100),
  lastName: z.string().min(1, "Last name is required").max(100),
  email: emailSchema,
  phone: phoneSchema,
  dateOfBirth: dateStringSchema.optional(),
  hireDate: dateStringSchema,
  employmentStatus: employmentStatusSchema.default("active"),
  employmentType: employmentTypeSchema,
  departmentId: uuidSchema.optional(),
  positionId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  seniorityLevelId: uuidSchema.optional(),
  managerId: uuidSchema.optional(),
  managerIds: z.array(uuidSchema).optional()
});
var updateEmployeeSchema = createEmployeeSchema.partial().extend({
  terminationDate: dateStringSchema.optional(),
  managerIds: z.array(uuidSchema).optional()
});
var employeeFilterSchema = z.object({
  search: z.string().optional(),
  departmentId: uuidSchema.optional(),
  positionId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  employmentStatus: employmentStatusSchema.optional(),
  employmentType: employmentTypeSchema.optional(),
  managerId: uuidSchema.optional()
});
var createDepartmentSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  parentId: uuidSchema.optional(),
  managerId: uuidSchema.optional()
});
var updateDepartmentSchema = createDepartmentSchema.partial();
var createPositionSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional()
});
var updatePositionSchema = createPositionSchema.partial();
var createLocationSchema = z.object({
  name: z.string().min(1).max(100),
  address: z.string().max(200).optional(),
  city: z.string().max(100).optional(),
  country: z.string().max(100).optional(),
  timezone: z.string().max(50).optional()
});
var updateLocationSchema = createLocationSchema.partial();
var createSeniorityLevelSchema = z.object({
  name: z.string().min(1).max(100),
  level: z.number().int().positive()
});
var updateSeniorityLevelSchema = createSeniorityLevelSchema.partial();
var absenceStatusSchema = z.enum(["pending", "approved", "rejected", "cancelled"]);
var createAbsenceTypeSchema = z.object({
  name: z.string().min(1).max(100),
  isPaid: z.boolean().default(true),
  defaultDays: z.number().int().nonnegative().default(0),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Color must be a valid hex color").optional()
});
var updateAbsenceTypeSchema = createAbsenceTypeSchema.partial();
var createAbsenceSchema = z.object({
  employeeId: uuidSchema,
  absenceTypeId: uuidSchema,
  startDate: dateStringSchema,
  endDate: dateStringSchema,
  notes: z.string().max(1e3).optional()
}).refine((data) => new Date(data.startDate) <= new Date(data.endDate), {
  message: "End date must be after or equal to start date",
  path: ["endDate"]
});
var updateAbsenceSchema = z.object({
  absenceTypeId: uuidSchema.optional(),
  startDate: dateStringSchema.optional(),
  endDate: dateStringSchema.optional(),
  notes: z.string().max(1e3).optional(),
  status: absenceStatusSchema.optional()
});
var absenceFilterSchema = z.object({
  employeeId: uuidSchema.optional(),
  absenceTypeId: uuidSchema.optional(),
  status: absenceStatusSchema.optional(),
  startDate: dateStringSchema.optional(),
  endDate: dateStringSchema.optional(),
  year: z.coerce.number().int().min(2e3).max(2100).optional()
});
var absenceCalendarQuerySchema = z.object({
  startDate: dateStringSchema,
  endDate: dateStringSchema,
  departmentId: uuidSchema.optional()
});
var createRoleSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters").max(100),
  description: z.string().max(500).optional(),
  permissionIds: z.array(uuidSchema).optional()
});
var updateRoleSchema = z.object({
  name: z.string().min(2).max(100).optional(),
  description: z.string().max(500).optional().nullable()
});
var assignPermissionsSchema = z.object({
  permissionIds: z.array(uuidSchema)
});
var createPermissionSchema = z.object({
  code: z.string().min(3).max(200).regex(
    /^[a-z][a-z0-9]*(\.[a-z][a-z0-9]*)*$/,
    'Code must use dot notation with lowercase letters (e.g., "module.resource.action")'
  ),
  name: z.string().min(2).max(200),
  description: z.string().max(500).optional(),
  module: z.string().min(2).max(100)
});
var updatePermissionSchema = z.object({
  name: z.string().min(2).max(200).optional(),
  description: z.string().max(500).optional().nullable()
});
var projectStatusSchema = z.enum(["active", "on_hold", "archived"]);
var projectSourceSchema = z.enum(["peopleforce", "manual"]);
var createProjectSchema = z.object({
  name: z.string().min(1, "Project name is required").max(100),
  slug: z.string().min(1).max(6).regex(/^[A-Z0-9]+$/, "Slug must be uppercase alphanumeric"),
  description: z.string().max(1e3).optional(),
  managerId: uuidSchema,
  status: projectStatusSchema.default("active"),
  memberIds: z.array(uuidSchema).optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Color must be a valid hex color").optional()
});
var updateProjectSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(1e3).nullable().optional(),
  managerId: uuidSchema.nullable().optional(),
  status: projectStatusSchema.optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional()
});
var projectQuerySchema = z.object({
  status: projectStatusSchema.optional(),
  source: projectSourceSchema.optional(),
  managerId: uuidSchema.optional(),
  search: z.string().optional(),
  sort: z.enum(["name", "status", "createdAt", "memberCount"]).optional(),
  order: z.enum(["asc", "desc"]).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20)
});
var addProjectMembersSchema = z.object({
  employeeIds: z.array(uuidSchema).min(1, "At least one employee is required")
});
var rosterMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Month must be in YYYY-MM format");
var upsertRosterSchema = z.object({
  month: rosterMonthSchema,
  entries: z.array(
    z.object({
      employeeId: uuidSchema,
      realHours: z.number().min(0).optional(),
      billableHours: z.number().min(0).optional(),
      niceToHaveRate: z.number().min(0).optional(),
      realRate: z.number().min(0).optional()
    })
  )
});
var projectSyncSettingsSchema = z.object({
  enabled: z.boolean().optional(),
  frequencyMinutes: z.number().int().min(15).max(1440).optional()
});
var assetStatusSchema = z.enum(["ASSIGNED", "RETURNED", "LOST", "BROKEN", "IN_REPAIR"]);
var createAssetTypeSchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  icon: z.string().max(50).optional(),
  description: z.string().optional(),
  fields: z.record(z.unknown()).optional()
});
var updateAssetTypeSchema = createAssetTypeSchema.partial();
var createAssetSchema = z.object({
  assetTypeId: uuidSchema,
  employeeId: uuidSchema,
  name: z.string().min(1, "Name is required").max(200),
  identifier: z.string().max(200).optional(),
  assignedAt: z.string().datetime().optional(),
  notes: z.string().optional(),
  customFields: z.record(z.unknown()).optional()
});
var updateAssetSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  identifier: z.string().max(200).nullable().optional(),
  status: assetStatusSchema.optional(),
  assignedAt: z.string().datetime().optional(),
  notes: z.string().nullable().optional(),
  customFields: z.record(z.unknown()).nullable().optional()
});
var accessGrantStatusSchema = z.enum(["ACTIVE", "REVOKED", "SUSPENDED", "EXPIRED"]);
var createAccessTypeSchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  icon: z.string().max(50).optional(),
  description: z.string().optional(),
  fields: z.record(z.unknown()).optional()
});
var updateAccessTypeSchema = createAccessTypeSchema.partial();
var createAccessGrantSchema = z.object({
  accessTypeId: uuidSchema,
  employeeId: uuidSchema,
  accountEmail: z.string().max(255).optional(),
  identifier: z.string().max(200).optional(),
  role: z.string().min(1, "Role is required").max(100),
  grantedAt: z.string().datetime().optional(),
  expiresAt: z.string().datetime().nullable().optional(),
  notes: z.string().optional(),
  customFields: z.record(z.unknown()).optional()
});
var updateAccessGrantSchema = z.object({
  accountEmail: z.string().max(255).nullable().optional(),
  identifier: z.string().max(200).nullable().optional(),
  role: z.string().max(100).nullable().optional(),
  status: accessGrantStatusSchema.optional(),
  expiresAt: z.string().datetime().nullable().optional(),
  notes: z.string().nullable().optional(),
  customFields: z.record(z.unknown()).nullable().optional()
});
var employeeProfileSectionSchema = z.enum([
  "JOB",
  "EMERGENCY",
  "DOCUMENTS",
  "ASSETS",
  "ACCESS",
  "NOTES",
  "ABSENCES",
  "COMPENSATIONS"
]);
var blockNameSchema = z.string().trim().min(1, "Block name is required").max(100, "Block name must be at most 100 characters");
var createEmployeeProfileBlockSchema = z.object({
  section: employeeProfileSectionSchema,
  name: blockNameSchema
});
var renameEmployeeProfileBlockSchema = z.object({
  name: blockNameSchema
});
var reorderEmployeeProfileBlocksSchema = z.object({
  section: employeeProfileSectionSchema,
  blockIds: z.array(uuidSchema).min(1, "blockIds must not be empty")
});
var employeeProfileFieldTypeSchema = z.enum([
  "SHORT_TEXT",
  "LONG_TEXT",
  "EMAIL",
  "PHONE",
  "URL",
  "NUMBER",
  "DATE",
  "DROPDOWN",
  "CHECKBOX",
  "FILE"
]);
var fieldNameSchema = z.string().trim().min(1, "Field name is required").max(100, "Field name must be at most 100 characters");
var descriptionSchema = z.string().trim().max(500, "Description must be at most 500 characters").nullable().optional();
var optionsSchema = z.array(z.string().trim().min(1, "Option cannot be empty").max(100)).min(2, "DROPDOWN must have at least 2 options").max(50, "DROPDOWN supports at most 50 options").nullable().optional();
var baseField = z.object({
  name: fieldNameSchema,
  description: descriptionSchema,
  type: employeeProfileFieldTypeSchema,
  required: z.boolean().optional(),
  options: optionsSchema
});
var createEmployeeProfileBlockFieldSchema = baseField.superRefine((data, ctx) => {
  if (data.type === "DROPDOWN") {
    if (!data.options || data.options.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["options"],
        message: "DROPDOWN requires at least 2 options"
      });
    } else {
      const lowered = data.options.map((o) => o.toLowerCase());
      if (new Set(lowered).size !== lowered.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["options"],
          message: "DROPDOWN options must be unique"
        });
      }
    }
  }
});
var updateEmployeeProfileBlockFieldSchema = baseField.partial();
var reorderEmployeeProfileBlockFieldsSchema = z.object({
  fieldIds: z.array(uuidSchema).min(1, "fieldIds must not be empty")
});
var setEmployeeProfileFieldValueSchema = z.object({
  value: z.string().max(5e3, "Value too long")
});
var preboardingFormTypeSchema = z.enum(["PREBOARDING"]);
var preboardingFormStatusSchema = z.enum(["DRAFT", "PUBLISHED", "ARCHIVED"]);
var formNameSchema = z.string().trim().min(1, "Form name is required").max(200, "Form name must be at most 200 characters");
var formDescriptionSchema = z.string().trim().max(1e3, "Description must be at most 1000 characters");
var emailContentSchema = z.object({
  subject: z.string().max(300).default(""),
  body: z.string().max(2e4).default("")
});
var preboardingFormEmailsConfigSchema = z.object({
  invitation: emailContentSchema,
  reminder: z.object({
    enabled: z.boolean().default(false),
    days: z.number().int().min(1).max(60).default(3),
    subject: z.string().max(300).default(""),
    body: z.string().max(2e4).default("")
  }),
  // When set, a PUBLISHED form is auto-sent to a candidate the moment they are
  // converted to an Employee (HW-585). Forms without this opt-in are sent
  // manually by HR.
  autoSendOnConversion: z.boolean().default(false)
});
var preboardingFormWelcomeConfigSchema = z.object({
  coverImageUrl: z.string().max(500).nullable().default(null),
  greetingTitle: z.string().max(120).default(""),
  welcomeMessage: z.string().max(2e4).default("")
});
var preboardingFormCompletionConfigSchema = z.object({
  successTitle: z.string().max(120).default(""),
  closingMessage: z.string().max(2e4).default(""),
  feedbackLink: z.object({
    enabled: z.boolean().default(false),
    label: z.string().max(80).default(""),
    url: z.string().max(2e3).default("")
  }).refine((link) => !link.enabled || /^https?:\/\/.+/.test(link.url), {
    path: ["url"],
    message: "Enter a valid URL (https://\u2026) for the feedback link"
  })
});
var createPreboardingFormSchema = z.object({
  type: preboardingFormTypeSchema,
  name: formNameSchema.optional()
});
var updatePreboardingFormSchema = z.object({
  name: formNameSchema.optional(),
  description: formDescriptionSchema.nullable().optional(),
  status: preboardingFormStatusSchema.optional(),
  emailsConfig: preboardingFormEmailsConfigSchema.nullable().optional(),
  welcomeConfig: preboardingFormWelcomeConfigSchema.nullable().optional(),
  completionConfig: preboardingFormCompletionConfigSchema.nullable().optional()
});
var preboardingFormFieldKindSchema = z.enum(["MAPPED", "DOCUMENT"]);
var sectionTitleSchema = z.string().trim().min(1, "Section title is required").max(200, "Section title must be at most 200 characters");
var sectionDescriptionSchema = z.string().trim().max(2e3, "Description must be at most 2000 characters");
var fieldLabelSchema = z.string().trim().min(1, "Field label is required").max(200, "Field label must be at most 200 characters");
var fieldHelperTextSchema = z.string().trim().max(2e3, "Helper text must be at most 2000 characters");
var mappingKeySchema = z.string().trim().min(1).max(200, "Mapping key must be at most 200 characters");
var targetFolderSchema = z.string().trim().min(1, "Folder is required").max(255, "Folder name must be at most 255 characters");
var createPreboardingFormSectionSchema = z.object({
  title: sectionTitleSchema,
  description: sectionDescriptionSchema.nullable().optional()
});
var updatePreboardingFormSectionSchema = z.object({
  title: sectionTitleSchema.optional(),
  description: sectionDescriptionSchema.nullable().optional()
}).refine((value) => Object.keys(value).length > 0, {
  message: "At least one field must be provided"
});
var reorderPreboardingFormSectionsSchema = z.object({
  sectionIds: z.array(z.string().uuid()).min(1)
});
var createPreboardingFormFieldSchema = z.object({
  kind: preboardingFormFieldKindSchema,
  label: fieldLabelSchema,
  helperText: fieldHelperTextSchema.nullable().optional(),
  required: z.boolean().optional().default(false),
  mappingKey: mappingKeySchema.nullable().optional(),
  targetFolder: targetFolderSchema.nullable().optional()
}).superRefine((value, ctx) => {
  if (value.kind === "MAPPED" && !value.mappingKey) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["mappingKey"],
      message: "mappingKey is required for a MAPPED field"
    });
  }
  if (value.kind === "DOCUMENT" && !value.targetFolder) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["targetFolder"],
      message: "targetFolder is required for a DOCUMENT field"
    });
  }
});
var updatePreboardingFormFieldSchema = z.object({
  label: fieldLabelSchema.optional(),
  helperText: fieldHelperTextSchema.nullable().optional(),
  required: z.boolean().optional(),
  mappingKey: mappingKeySchema.nullable().optional(),
  targetFolder: targetFolderSchema.nullable().optional()
}).refine((value) => Object.keys(value).length > 0, {
  message: "At least one field must be provided"
});
var reorderPreboardingFormFieldsSchema = z.object({
  fieldIds: z.array(z.string().uuid()).min(1)
});
var publicPreboardingFormAccessSchema = z.object({
  email: z.string().trim().toLowerCase().email("Enter a valid email").max(320)
});
var resolvePreboardingConflictSchema = z.object({
  resolution: z.enum(["OVERWRITE", "KEEP"])
});
var SCHEDULE_KEYS = ["people_events", "probation_ending", "whos_out_digest"];
var notificationScheduleSchema = z.object({
  key: z.enum(SCHEDULE_KEYS),
  leadDays: z.array(z.number().int().min(0).max(30)).min(1).max(6),
  sendTime: z.string().regex(/^([01]\d|2[0-3]):00$/, 'on-the-hour, e.g. "09:00"'),
  enabled: z.boolean()
});
var updateNotificationScheduleSchema = notificationScheduleSchema.pick({ leadDays: true, sendTime: true }).extend({ enabled: z.boolean().optional() });

// src/automation/condition-fields.ts
var NO_VALUE_OPERATORS = ["isEmpty", "isNotEmpty", "overdue"];
function conditionValueKind(field, operator) {
  if (NO_VALUE_OPERATORS.includes(operator)) return "none";
  switch (field) {
    case "status":
      return "status";
    case "statusCategory":
    case "priority":
    case "type":
      return "enum";
    case "assignee":
    case "reporter":
      return "member";
    case "label":
      return "tag";
    case "storyPoints":
      return "number";
    case "dueDate":
      return operator === "withinDays" ? "days" : "none";
    case "text":
      return "text";
    case "hasParent":
    case "inSprint":
      return "boolean";
    default:
      return "text";
  }
}
function defaultConditionDraft() {
  return { field: "", operator: "eq", value: void 0 };
}
var STATUS_CATEGORY_OPTIONS = [
  { value: "backlog", label: "Backlog" },
  { value: "active", label: "Active" },
  { value: "done", label: "Done" },
  { value: "cancelled", label: "Cancelled" }
];
var PRIORITY_OPTIONS = [
  { value: "none", label: "None" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" }
];
var TYPE_OPTIONS = [
  { value: "task", label: "Task" },
  { value: "bug", label: "Bug" },
  { value: "story", label: "Story" },
  { value: "epic", label: "Epic" }
];

// src/schemas/automation.ts
var isBlank = (v) => v === void 0 || v === null || v === "";
var StatusCategoryEnum = z.enum(["backlog", "active", "done", "cancelled"]);
var TaskFieldEnum = z.enum(["priority", "type", "dueDate", "storyPoints", "labels"]);
var TriggerSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("task.created") }),
  z.object({
    type: z.literal("task.moved"),
    toStatusId: z.string().optional(),
    toCategory: StatusCategoryEnum.optional()
  }),
  z.object({
    type: z.literal("task.assigned"),
    toAssigneeId: z.string().optional()
  }),
  z.object({ type: z.literal("task.field_changed"), field: TaskFieldEnum }),
  z.object({ type: z.literal("comment.added") }),
  z.object({ type: z.literal("task.due"), offset: z.enum(["24h", "1h", "overdue"]) })
]);
var ConditionFieldEnum = z.enum([
  "status",
  "statusCategory",
  "priority",
  "type",
  "assignee",
  "reporter",
  "label",
  "storyPoints",
  "dueDate",
  "text",
  "hasParent",
  "inSprint"
]);
var OperatorEnum = z.enum([
  "eq",
  "neq",
  "in",
  "isEmpty",
  "isNotEmpty",
  "gt",
  "lt",
  "contains",
  "overdue",
  "withinDays"
]);
var ConditionSchema = z.object({
  field: ConditionFieldEnum,
  operator: OperatorEnum,
  value: z.unknown().optional()
}).superRefine((c, ctx) => {
  if (conditionValueKind(c.field, c.operator) === "none") return;
  if (isBlank(c.value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["value"],
      message: `Condition "${c.field} ${c.operator}" requires a value`
    });
  }
});
var ConditionsSchema = z.object({
  match: z.enum(["all", "any"]),
  rules: z.array(ConditionSchema).max(10)
});
var SettableFieldEnum = z.enum([
  "status",
  "priority",
  "type",
  "assignee",
  "dueDate",
  "storyPoints"
]);
var NotifyAudienceEnum = z.enum(["assignee", "reporter", "person"]);
var AiPresetEnum = z.enum([
  "triage",
  "summarize_comments",
  "draft_acceptance_criteria",
  "custom"
]);
var ActionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("set_field"), field: SettableFieldEnum, value: z.unknown() }),
  z.object({
    type: z.literal("assign"),
    mode: z.enum(["person", "round_robin", "least_loaded"]),
    assigneeId: z.string().optional()
  }),
  z.object({
    type: z.literal("label"),
    op: z.enum(["add", "remove"]),
    tagId: z.string().min(1, "Pick a label")
  }),
  z.object({ type: z.literal("move_status"), toStatusId: z.string().min(1, "Pick a status") }),
  z.object({
    type: z.literal("add_to_sprint"),
    sprint: z.union([z.literal("active"), z.string().min(1, "Pick a sprint")])
  }),
  z.object({ type: z.literal("comment"), template: z.string().min(1) }),
  z.object({
    type: z.literal("notify"),
    audience: NotifyAudienceEnum,
    channels: z.array(z.literal("in_app")).min(1),
    personId: z.string().optional()
  }),
  z.object({ type: z.literal("create_subtask"), titles: z.array(z.string().min(1)).min(1) }),
  z.object({
    type: z.literal("ask_ai"),
    preset: AiPresetEnum,
    prompt: z.string().optional(),
    output: z.enum(["apply", "comment"])
  }),
  z.object({
    type: z.literal("webhook"),
    url: z.string().url(),
    includeTask: z.boolean().default(true)
  })
]).superRefine((a, ctx) => {
  if (a.type === "set_field" && isBlank(a.value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["value"],
      message: `set_field "${a.field}" requires a non-empty value`
    });
  }
  if (a.type === "assign" && a.mode === "person" && (!a.assigneeId || a.assigneeId === "")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["assigneeId"],
      message: "Pick a person to assign"
    });
  }
  if (a.type === "notify" && a.audience === "person" && (!a.personId || a.personId === "")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["personId"],
      message: "Pick a person to notify"
    });
  }
});
var AutomationRuleSchema = z.object({
  trigger: TriggerSchema,
  conditions: ConditionsSchema,
  actions: z.array(ActionSchema).min(1).max(10)
});
var TimeReportPeriodPresetEnum = z.enum([
  "this-week",
  "last-week",
  "this-month",
  "last-month",
  "custom"
]);
var TimeReportPeriodSchema = z.object({
  preset: TimeReportPeriodPresetEnum,
  /** yyyy-MM-dd, inclusive. Read only when the preset is 'custom'. */
  from: z.string().optional(),
  to: z.string().optional()
}).superRefine((period, ctx) => {
  if (period.preset !== "custom") return;
  if (!period.from) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["from"],
      message: "A custom period needs a start date"
    });
  }
  if (!period.to) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["to"],
      message: "A custom period needs an end date"
    });
  }
});
var TimeReportBillableEnum = z.enum(["only", "exclude", "all"]);
var TimeReportProjectStatusEnum = z.enum(["active", "on_hold", "archived"]);
var TimeReportEntrySourceEnum = z.enum(["manual", "timer", "clockify", "tempo", "paper"]);
var TimeReportHasTaskEnum = z.enum(["only", "exclude", "all"]);
var TimeReportFiltersSchema = z.object({
  // An empty array is a valid "no filter" — the draft flow leaves the list
  // empty when a name it was given matches no project, and that must parse.
  projectIds: z.array(z.string()).optional(),
  employeeIds: z.array(z.string()).optional(),
  billable: TimeReportBillableEnum.default("all"),
  // People. These read the person's CURRENT department, position and
  // employment type — there is no history table for any of them, so hours are
  // attributed to where somebody sits today (design §6).
  departmentIds: z.array(z.string()).optional(),
  positionIds: z.array(z.string()).optional(),
  employmentTypeIds: z.array(z.string()).optional(),
  // Projects.
  projectStatuses: z.array(TimeReportProjectStatusEnum).optional(),
  /**
   * Managers as `ProjectMember.isManager` records them, NOT
   * `Project.managerId` — the schema calls that one a legacy display-only
   * pointer, it holds a single manager where a project can have several, and
   * PeopleForce sync writes it without ever setting `isManager`.
   */
  projectManagerIds: z.array(z.string()).optional(),
  // Entries.
  entrySources: z.array(TimeReportEntrySourceEnum).optional(),
  /**
   * Left UNDEFAULTED, unlike `billable`.
   *
   * Billable is a control the builder always shows, so resolving it to 'all'
   * keeps one spelling of "everything" in every stored config. hasTask is one
   * of the filters that only appears once it is chosen; defaulting it would
   * write a constraint nobody configured into every saved report. Absent and
   * 'all' compile to the same absence of a clause, so there is still exactly
   * one behaviour.
   */
  hasTask: TimeReportHasTaskEnum.optional(),
  /**
   * Count only entries whose UTC month has already ended — the same boundary
   * the write path uses to refuse an edit (`month-cutoff.ts`), not a lock a
   * human sets and not an audit flag. No lock model exists yet; when one does,
   * this filter's meaning moves with that file and nothing here changes.
   *
   * Left undefaulted for the reason `hasTask` above is: it appears only once
   * somebody switches it on, and defaulting it would write a constraint nobody
   * configured into every saved report. Absent and `false` compile to the same
   * absent clause, so there is still exactly one behaviour — and a `false` the
   * builder never wrote is a `false` no reader has to interpret. [HW-850]
   */
  completedPeriodsOnly: z.boolean().optional()
});
var TimeReportGroupByEnum = z.enum([
  "project",
  "employee",
  "day",
  "week",
  // People attributes and the task axis. None of them is a time axis, so they
  // combine with each other and with a day or a week freely.
  "department",
  "task",
  "employmentType",
  "position"
]);
var TIME_AXES = /* @__PURE__ */ new Set(["day", "week"]);
var TimeReportGroupBySchema = z.array(TimeReportGroupByEnum).min(1, "Group the report by at least one thing").max(2, "Group the report by at most two things").superRefine((levels, ctx) => {
  if (new Set(levels).size !== levels.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      // Nesting a level under itself yields one child row per parent, every
      // time — a table that says nothing and looks like a bug.
      message: "Each grouping level may appear only once"
    });
    return;
  }
  if (levels.filter((level) => TIME_AXES.has(level)).length > 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Group by at most one time axis \u2014 a day and its week are the same axis twice"
    });
  }
});
var TimeReportMetricEnum = z.enum(["hours", "share", "dailyAverage"]);
var TimeReportViewEnum = z.enum(["table", "table+chart"]);
var TimeReportConfigSchema = z.object({
  period: TimeReportPeriodSchema,
  filters: TimeReportFiltersSchema.default({}),
  /** Ordered outer → inner. */
  groupBy: TimeReportGroupBySchema,
  metrics: z.array(TimeReportMetricEnum).min(1, "Pick at least one column"),
  view: TimeReportViewEnum
});
var DEFAULT_CONFIG = {
  period: { preset: "this-month" },
  filters: { billable: "all" },
  groupBy: ["project"],
  metrics: ["hours", "share"],
  view: "table"
};
var DEFAULT_TIME_REPORT_CONFIG = Object.freeze({
  ...DEFAULT_CONFIG,
  period: Object.freeze(DEFAULT_CONFIG.period),
  filters: Object.freeze(DEFAULT_CONFIG.filters),
  // `Object.freeze` widens an array to `readonly T[]`, which the config type
  // does not accept; the cast keeps the published type unchanged.
  groupBy: Object.freeze(DEFAULT_CONFIG.groupBy),
  metrics: Object.freeze(DEFAULT_CONFIG.metrics)
});

// src/constants/recruitment-file-types.ts
var DOC_MIMES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];
var IMAGE_MIMES = ["image/png", "image/jpeg", "image/webp"];
var ZIP_MIMES = ["application/zip", "application/x-zip-compressed"];
var DOC_EXTS = [".pdf", ".docx"];
var IMAGE_EXTS = [".png", ".jpg", ".jpeg", ".webp"];
var ZIP_EXTS = [".zip"];
var RECRUITMENT_UPLOAD_MIMES_BY_SLOT = {
  CV: [...DOC_MIMES, ...IMAGE_MIMES],
  TEST_TASK: [...DOC_MIMES, ...ZIP_MIMES, ...IMAGE_MIMES],
  PORTFOLIO: [...DOC_MIMES, ...ZIP_MIMES, ...IMAGE_MIMES],
  OTHER: [...DOC_MIMES, ...ZIP_MIMES, ...IMAGE_MIMES]
};
var RECRUITMENT_UPLOAD_EXTS_BY_SLOT = {
  CV: [...DOC_EXTS, ...IMAGE_EXTS],
  TEST_TASK: [...DOC_EXTS, ...ZIP_EXTS, ...IMAGE_EXTS],
  PORTFOLIO: [...DOC_EXTS, ...ZIP_EXTS, ...IMAGE_EXTS],
  OTHER: [...DOC_EXTS, ...ZIP_EXTS, ...IMAGE_EXTS]
};
var RECRUITMENT_UPLOAD_HINT_BY_SLOT = {
  CV: "PDF, DOCX or image (PNG, JPEG, WebP)",
  TEST_TASK: "PDF, DOCX, ZIP or image (PNG, JPEG, WebP)",
  PORTFOLIO: "PDF, DOCX, ZIP or image (PNG, JPEG, WebP)",
  OTHER: "PDF, DOCX, ZIP or image (PNG, JPEG, WebP)"
};
var RECRUITMENT_UPLOAD_MIME_TYPES = [
  ...DOC_MIMES,
  ...ZIP_MIMES,
  ...IMAGE_MIMES
];
var RECRUITMENT_PREVIEW_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
  "image/webp"
];
var RECRUITMENT_PARSER_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];
var RECRUITMENT_PARSER_EXTENSIONS = [".pdf", ".docx"];
var RECRUITMENT_PARSER_HINT = "PDF or DOCX, max 20 MB";
var RECRUITMENT_PARSER_EXTENSION_REGEX = /\.(pdf|docx)$/i;
var RECRUITMENT_FILE_MAX_SIZE_BYTES = 20 * 1024 * 1024;
function isImageMime(mimeType) {
  return mimeType === "image/png" || mimeType === "image/jpeg" || mimeType === "image/webp";
}

// src/constants/html-sanitize.ts
var RICH_TEXT_HTML_ALLOWLIST = {
  ALLOWED_TAGS: [
    "p",
    // HW-704: Tiptap StarterKit's HorizontalRule renders <hr>; keep it so it
    // survives save-time sanitization instead of being silently dropped.
    "hr",
    "br",
    "strong",
    "b",
    "em",
    "i",
    "u",
    // HW-704: the editor's Strike mark renders <s> (not <strike>); include it
    // alongside the legacy strike/del synonyms so strikethrough round-trips.
    "s",
    "strike",
    "del",
    "a",
    "ul",
    "ol",
    "li",
    "blockquote",
    "code",
    "pre",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "span",
    "div",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td"
  ],
  ALLOWED_ATTR: ["href", "target", "class", "style"],
  ALLOW_DATA_ATTR: false
};

// src/constants/employee-finance.ts
var PAYMENT_CURRENCIES = ["UAH", "USD", "EUR", "GBP", "PLN"];
var TAX_ID_PATTERN = /^\d{10}$/;
var SWIFT_PATTERN = /^[A-Z0-9]{8}([A-Z0-9]{3})?$/;
var KVED_PATTERN = /^\d{2}\.\d{2}$/;
function normalizeIban(value) {
  return value.replace(/\s+/g, "").toUpperCase();
}
function isValidUaIban(value) {
  const iban = normalizeIban(value);
  if (!/^UA\d{8}[A-Z0-9]{19}$/.test(iban)) return false;
  const rearranged = (iban.slice(4) + iban.slice(0, 4)).replace(
    /[A-Z]/g,
    (c) => String(c.charCodeAt(0) - 55)
  );
  let remainder = 0;
  for (const digit of rearranged) remainder = (remainder * 10 + Number(digit)) % 97;
  return remainder === 1;
}
var EDUCATION_TYPES = [
  "secondary",
  "vocational",
  "bachelor",
  "master",
  "phd",
  "courses"
];
var EDUCATION_TYPE_LABELS = {
  secondary: "Secondary",
  vocational: "Vocational",
  bachelor: "Bachelor",
  master: "Master",
  phd: "PhD",
  courses: "Courses / Certification"
};
var MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
var monthYear = (day) => `${MONTHS[Number(day.slice(5, 7)) - 1] ?? "?"} ${day.slice(0, 4)}`;
function formatEducationPeriod(start, end) {
  if (!start && !end) return "";
  return `${start ? monthYear(start) : "\u2026"} \u2013 ${end ? monthYear(end) : "present"}`;
}
function describeEducation(entry) {
  const type = EDUCATION_TYPE_LABELS[entry.type] ?? entry.type;
  const where = entry.specialty ? `${entry.institution}, ${entry.specialty}` : entry.institution;
  const period = formatEducationPeriod(entry.startDate, entry.endDate);
  return [type, where, period].filter(Boolean).join(" \xB7 ");
}

// src/constants/index.ts
var PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100
};
var USER_ROLES = {
  SUPER_ADMIN: "super_admin",
  HR_ADMIN: "hr_admin",
  HR_MANAGER: "hr_manager",
  MANAGER: "manager",
  EMPLOYEE: "employee"
};
var USER_ROLE_LABELS = {
  [USER_ROLES.SUPER_ADMIN]: "Super Admin",
  [USER_ROLES.HR_ADMIN]: "HR Admin",
  [USER_ROLES.HR_MANAGER]: "HR Manager",
  [USER_ROLES.MANAGER]: "Manager",
  [USER_ROLES.EMPLOYEE]: "Employee"
};
var EMPLOYEE_STATUS = {
  ACTIVE: "active",
  PROBATION: "probation",
  ONBOARDING: "onboarding",
  TERMINATED: "terminated"
};
var EMPLOYMENT_TYPE = {
  FULL_TIME: "full_time",
  PART_TIME: "part_time",
  CONTRACT: "contract",
  INTERN: "intern"
};
var ABSENCE_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  CANCELLED: "cancelled"
};
var PROJECT_STATUS = {
  ACTIVE: "active",
  ON_HOLD: "on_hold",
  ARCHIVED: "archived"
};
var PROJECT_STATUS_LABELS = {
  [PROJECT_STATUS.ACTIVE]: "Active",
  [PROJECT_STATUS.ON_HOLD]: "On Hold",
  [PROJECT_STATUS.ARCHIVED]: "Archived"
};
var API_ENDPOINTS = {
  AUTH: {
    LOGIN: "/auth/google",
    CALLBACK: "/auth/google/callback",
    REFRESH: "/auth/refresh",
    LOGOUT: "/auth/logout",
    ME: "/auth/me",
    CHECK: "/auth/check"
  },
  EMPLOYEES: "/employees",
  DEPARTMENTS: "/departments",
  POSITIONS: "/positions",
  LOCATIONS: "/locations",
  SENIORITY_LEVELS: "/seniority-levels",
  ABSENCES: "/absences",
  ABSENCE_TYPES: "/absence-types",
  PROJECTS: "/projects",
  ROLES: "/roles",
  PERMISSIONS: "/permissions"
};
var CACHE_TIME = {
  /** 5 minutes in milliseconds */
  DEFAULT: 1e3 * 60 * 5,
  /** 1 minute in milliseconds */
  SHORT: 1e3 * 60,
  /** 15 minutes in milliseconds */
  LONG: 1e3 * 60 * 15
};

// src/automation/rule-summary.ts
var nm = (map, id) => id && map?.[id] || id || "\u2014";
function triggerText(t, l) {
  switch (t.type) {
    case "task.created":
      return "Task is created";
    case "task.moved":
      return t.toStatusId ? `Task moved to ${nm(l.status, t.toStatusId)}` : t.toCategory ? `Task moved to ${t.toCategory}` : "Task moves status";
    case "task.assigned":
      return t.toAssigneeId ? `Task assigned to ${nm(l.member, t.toAssigneeId)}` : "Task is assigned";
    case "task.field_changed":
      return `Task ${t.field} changes`;
    case "comment.added":
      return "A comment is added";
    case "task.due":
      return t.offset === "overdue" ? "Task becomes overdue" : `Task due in ${t.offset}`;
    default:
      return t.type;
  }
}
var OP_TEXT = {
  eq: "is",
  neq: "is not",
  in: "is one of",
  isEmpty: "is empty",
  isNotEmpty: "is set",
  gt: ">",
  lt: "<",
  contains: "contains",
  overdue: "is overdue",
  withinDays: "within days"
};
var FIELD_TEXT = {
  status: "Status",
  statusCategory: "Status category",
  priority: "Priority",
  type: "Type",
  assignee: "Assignee",
  reporter: "Reporter",
  label: "Label",
  storyPoints: "Story points",
  dueDate: "Due date",
  text: "Text",
  hasParent: "Has parent",
  inSprint: "In sprint"
};
function conditionText(c, l) {
  const field = FIELD_TEXT[c.field] ?? c.field;
  const op = OP_TEXT[c.operator] ?? c.operator;
  if (c.operator === "isEmpty" || c.operator === "isNotEmpty" || c.operator === "overdue") {
    return `${field} ${op}`;
  }
  let val;
  if (c.field === "status") val = nm(l.status, c.value);
  else if (c.field === "assignee" || c.field === "reporter") val = nm(l.member, c.value);
  else if (c.field === "label") val = nm(l.tag, c.value);
  else val = c.value == null ? "" : String(c.value);
  return `${field} ${op} ${val}`.trim();
}
function actionText(a, l) {
  switch (a.type) {
    case "set_field":
      return `Set ${a.field}`;
    case "assign":
      return a.mode === "person" ? `Assign ${nm(l.member, a.assigneeId)}` : `Assign ${a.mode === "round_robin" ? "round-robin" : "least-loaded"}`;
    case "label":
      return `${a.op === "add" ? "Add" : "Remove"} label ${nm(l.tag, a.tagId)}`;
    case "move_status":
      return `Move to ${nm(l.status, a.toStatusId)}`;
    case "add_to_sprint":
      return a.sprint === "active" ? "Add to active sprint" : "Add to sprint";
    case "comment":
      return "Post a comment";
    case "notify": {
      const ch = a.channels.map((c) => c === "in_app" ? "in-app" : c).join(", ");
      const who = a.audience === "person" ? nm(l.member, a.personId) : a.audience;
      return `Notify ${who} (${ch})`;
    }
    case "create_subtask":
      return `Create ${a.titles.length} subtask${a.titles.length === 1 ? "" : "s"}`;
    case "ask_ai":
      return `Ask AI (${a.preset}) \u2192 ${a.output === "apply" ? "apply" : "comment"}`;
    case "webhook":
      return "Call webhook";
  }
}
function summarizeRule(rule, lookups = {}) {
  return {
    when: triggerText(rule.trigger, lookups),
    match: rule.conditions.match,
    conditions: rule.conditions.rules.map((c) => conditionText(c, lookups)),
    alwaysRuns: rule.conditions.rules.length === 0,
    actions: rule.actions.map((a) => actionText(a, lookups))
  };
}

// src/charts/time-series.ts
var DAY_MS = 24 * 60 * 60 * 1e3;
function toDayKey(d) {
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function bucketByWeek(days) {
  const weeks = /* @__PURE__ */ new Map();
  for (const day of days) {
    const [year, month, date] = day.date.split("-").map(Number);
    const utc = new Date(Date.UTC(year, month - 1, date));
    const sinceMonday = (utc.getUTCDay() + 6) % 7;
    const monday = toDayKey(new Date(utc.getTime() - sinceMonday * DAY_MS));
    weeks.set(monday, (weeks.get(monday) ?? 0) + day.totalSeconds);
  }
  return Array.from(weeks, ([date, totalSeconds]) => ({ date, totalSeconds })).sort(
    (a, b) => a.date.localeCompare(b.date)
  );
}
function sharePercents(values) {
  const total = values.reduce((sum, v) => sum + v, 0);
  if (values.length === 0 || total <= 0) return values.map(() => 0);
  const exact = values.map((v) => v / total * 100);
  const shares = exact.map((v) => Math.floor(v));
  let leftover = 100 - shares.reduce((sum, v) => sum + v, 0);
  const byRemainder = exact.map((v, index) => ({ index, remainder: v - Math.floor(v) })).sort((a, b) => b.remainder - a.remainder || b.index - a.index);
  for (const { index } of byRemainder) {
    if (leftover <= 0) break;
    shares[index] += 1;
    leftover -= 1;
  }
  return shares;
}

// src/time/zoned-time.ts
var ZONE_NAME = /^[A-Za-z][A-Za-z0-9_+\-/]*$/;
var formatters = /* @__PURE__ */ new Map();
var MAX_CACHED_ZONES = 512;
function formatterFor(zone) {
  const key = zone.toLowerCase();
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
    if (formatters.size >= MAX_CACHED_ZONES) formatters.clear();
    formatters.set(key, formatter);
  }
  return formatter;
}
function wallPartsIn(zone, instant) {
  const parts = formatterFor(zone).formatToParts(instant);
  const read = (type) => Number(parts.find((part) => part.type === type)?.value ?? NaN);
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: read("hour"),
    minute: read("minute"),
    second: read("second")
  };
}
function isTimeZoneNameShape(zone) {
  return typeof zone === "string" && zone.length > 0 && zone.length <= 64 && ZONE_NAME.test(zone);
}
function isValidTimeZone(zone) {
  if (!isTimeZoneNameShape(zone)) return false;
  try {
    formatterFor(zone);
    return true;
  } catch {
    return false;
  }
}
function readableZone(zone) {
  return zone && isValidTimeZone(zone) ? zone : "UTC";
}
var RENAMED_ZONES = {
  "Europe/Kiev": "Europe/Kyiv",
  "Europe/Uzhgorod": "Europe/Kyiv",
  "Europe/Zaporozhye": "Europe/Kyiv",
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Rangoon": "Asia/Yangon",
  "Africa/Asmera": "Africa/Asmara",
  "America/Godthab": "America/Nuuk",
  "America/Coral_Harbour": "America/Atikokan",
  "America/Indianapolis": "America/Indiana/Indianapolis",
  "America/Louisville": "America/Kentucky/Louisville",
  "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
  "America/Catamarca": "America/Argentina/Catamarca",
  "America/Cordoba": "America/Argentina/Cordoba",
  "America/Jujuy": "America/Argentina/Jujuy",
  "America/Mendoza": "America/Argentina/Mendoza",
  "Atlantic/Faeroe": "Atlantic/Faroe",
  "Pacific/Enderbury": "Pacific/Kanton",
  "Pacific/Truk": "Pacific/Chuuk",
  "Pacific/Ponape": "Pacific/Pohnpei"
};
function canonicalTimeZone(zone) {
  if (!isValidTimeZone(zone)) return zone;
  const resolved = formatterFor(zone).resolvedOptions().timeZone ?? zone;
  const renamed = RENAMED_ZONES[resolved];
  return renamed && isValidTimeZone(renamed) ? renamed : resolved;
}
function storedZoneOrNull(zone) {
  return zone && isValidTimeZone(zone) ? canonicalTimeZone(zone) : null;
}
function wallDigitsIn(zone, instant) {
  if (!isValidTimeZone(zone)) throw new RangeError(`Not an IANA time zone name: "${zone}"`);
  const w = wallPartsIn(zone, instant);
  return new Date(Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second));
}

// src/time/month-cutoff.ts
var MONTH_CLOSES_AT_UTC_HOUR = 6;
function monthIndex(d) {
  return d.getUTCFullYear() * 12 + d.getUTCMonth();
}
function runningMonthStart(now) {
  const shifted = new Date(now.getTime() - MONTH_CLOSES_AT_UTC_HOUR * 36e5);
  return new Date(Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), 1));
}
function monthCutoffClosed(when, now) {
  return monthIndex(when) < monthIndex(runningMonthStart(now));
}

// src/time/reopen-window.ts
var REOPEN_DURATION_HOURS = [1, 2, 4, 8, 24];
var REOPEN_DEFAULT_HOURS = 2;
var REOPEN_DEFAULT_LOOKBACK_DAYS = 7;
var DAY_MS2 = 864e5;
var HOUR_MS = 36e5;
function isReopenDuration(value) {
  return typeof value === "number" && REOPEN_DURATION_HOURS.includes(value);
}
function reopenDayKey(instant) {
  return instant.toISOString().slice(0, 10);
}
function isReopenDayKey(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = /* @__PURE__ */ new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && reopenDayKey(parsed) === value;
}
function reopenFromBounds(now) {
  const max = reopenDayKey(now);
  const min = reopenDayKey(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)));
  const weekAgo = reopenDayKey(new Date(now.getTime() - REOPEN_DEFAULT_LOOKBACK_DAYS * DAY_MS2));
  const monthStart = reopenDayKey(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)));
  const previousMonthLastWeek = reopenDayKey(
    new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0) - (REOPEN_DEFAULT_LOOKBACK_DAYS - 1) * DAY_MS2
    )
  );
  const initial = weekAgo < monthStart ? weekAgo : previousMonthLastWeek;
  return { min, max, initial: initial < min ? min : initial };
}
function reopenUntil(now, hours) {
  return new Date(now.getTime() + hours * HOUR_MS);
}
function reopenWindowLive(until, now) {
  if (until == null) return false;
  const at = typeof until === "string" ? Date.parse(until) : until.getTime();
  return Number.isFinite(at) && at > now.getTime();
}
function reopenWindowCoversDay(window, dayKey, now) {
  if (!window || !reopenWindowLive(window.until, now)) return false;
  return dayKey >= window.from && dayKey <= reopenDayKey(now);
}
function reopenDurationLabel(hours) {
  return hours === 24 ? "1 day" : `${hours}h`;
}
function reopenActivityLine(p) {
  const dayMonth = `${p.from.slice(8, 10)}.${p.from.slice(5, 7)}`;
  const by = p.byAccount ? " by an admin account" : "";
  return `Reopened the closed period for ${p.memberName} from ${dayMonth} until ${p.untilText} (${reopenDurationLabel(p.hours)})${by}`;
}

// src/time/consent-expiry.ts
var CONSENT_ENDING_SOON_DAYS = 30;
var DAY_MS3 = 864e5;
function consentDayKey(instant) {
  return instant.toISOString().slice(0, 10);
}
function dayKeyToUtcMs(day) {
  return Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
}
function consentEndDay(grantedAt) {
  const year = grantedAt.getUTCFullYear() + 1;
  const month = grantedAt.getUTCMonth();
  const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const day = Math.min(grantedAt.getUTCDate(), lastDayOfMonth);
  return consentDayKey(new Date(Date.UTC(year, month, day)));
}
function consentExpiry(endDay, today) {
  const daysLeft = Math.round((dayKeyToUtcMs(endDay) - dayKeyToUtcMs(today)) / DAY_MS3);
  const state = daysLeft <= 0 ? "EXPIRED" : daysLeft <= CONSENT_ENDING_SOON_DAYS ? "ENDING_SOON" : "ACTIVE";
  return { state, daysLeft };
}
function consentEndingSoonCutoffDay(today) {
  return consentDayKey(new Date(dayKeyToUtcMs(today) + CONSENT_ENDING_SOON_DAYS * DAY_MS3));
}

// src/permissions/grants-unscoped.ts
function grantsUnscoped(userPerm, required) {
  if (userPerm === "*") return true;
  if (userPerm === required) return true;
  if (userPerm.endsWith(".*")) {
    const prefix = userPerm.slice(0, -2);
    return required.startsWith(prefix + ".");
  }
  return false;
}

// src/permissions/super-admin-grant-only.ts
var SUPER_ADMIN_GRANT_ONLY_PERMISSIONS = [
  "settings.general.showPlaceholder",
  "settings.roles.manage",
  "settings.permissions.manage",
  "auth.impersonate"
];

export { ABSENCE_STATUS, API_ENDPOINTS, ActionSchema, AiPresetEnum, AutomationRuleSchema, BOARD_MEMBER_ROLES, BOARD_PERMISSIONS, BOARD_PERMISSION_VALUES, BOARD_ROLE_ORDER, BOARD_ROLE_PRESETS, BOARD_TEMPLATES, CACHE_TIME, CONSENT_ENDING_SOON_DAYS, ConditionFieldEnum, ConditionSchema, ConditionsSchema, DEFAULT_PREBOARDING_DOCUMENT_FOLDER, DEFAULT_TIME_REPORT_CONFIG, DEPARTMENT_TEMPLATE_MAP, EDUCATION_TYPES, EDUCATION_TYPE_LABELS, EMPLOYEE_PROFILE_FIELD_TYPES, EMPLOYEE_PROFILE_SECTIONS, EMPLOYEE_PROFILE_SECTION_LABELS, EMPLOYEE_STATUS, EMPLOYEE_STATUSES, EMPLOYMENT_TYPE, EMPLOYMENT_TYPES, KVED_PATTERN, MAPPING_KEY_CUSTOM_PREFIX, MAPPING_KEY_SYSTEM_PREFIX, MONTH_CLOSES_AT_UTC_HOUR, NO_VALUE_OPERATORS, NotifyAudienceEnum, OWNER_LOCKED_PERMISSIONS, OperatorEnum, PAGINATION, PAYMENT_CURRENCIES, PREBOARDING_CONFLICT_RESOLUTIONS, PREBOARDING_CONFLICT_STATUSES, PREBOARDING_FORM_FIELD_KINDS, PREBOARDING_FORM_STATUSES, PREBOARDING_FORM_TYPES, PREBOARDING_FORM_VARIABLES, PRIORITY_OPTIONS, PROJECT_ACTIVITY_TYPES, PROJECT_COLORS, PROJECT_STATUS, PROJECT_STATUS_LABELS, RECRUITMENT_FILE_MAX_SIZE_BYTES, RECRUITMENT_PARSER_EXTENSIONS, RECRUITMENT_PARSER_EXTENSION_REGEX, RECRUITMENT_PARSER_HINT, RECRUITMENT_PARSER_MIME_TYPES, RECRUITMENT_PREVIEW_MIME_TYPES, RECRUITMENT_UPLOAD_EXTS_BY_SLOT, RECRUITMENT_UPLOAD_HINT_BY_SLOT, RECRUITMENT_UPLOAD_MIMES_BY_SLOT, RECRUITMENT_UPLOAD_MIME_TYPES, REOPEN_DEFAULT_HOURS, REOPEN_DEFAULT_LOOKBACK_DAYS, REOPEN_DURATION_HOURS, RICH_TEXT_HTML_ALLOWLIST, SCHEDULE_KEYS, STATUS_CATEGORY_OPTIONS, SUPER_ADMIN_GRANT_ONLY_PERMISSIONS, SWIFT_PATTERN, SettableFieldEnum, StatusCategoryEnum, TAX_ID_PATTERN, TIME_ENTRY_SOURCES, TYPE_OPTIONS, TaskFieldEnum, TimeReportBillableEnum, TimeReportConfigSchema, TimeReportEntrySourceEnum, TimeReportFiltersSchema, TimeReportGroupByEnum, TimeReportGroupBySchema, TimeReportHasTaskEnum, TimeReportMetricEnum, TimeReportPeriodPresetEnum, TimeReportPeriodSchema, TimeReportProjectStatusEnum, TimeReportViewEnum, TriggerSchema, USER_ROLES, USER_ROLE_LABELS, USER_WRITABLE_TIME_ENTRY_SOURCES, absenceCalendarQuerySchema, absenceFilterSchema, absenceStatusSchema, accessGrantStatusSchema, addProjectMembersSchema, assetStatusSchema, assignPermissionsSchema, boardPermissionsFor, boardRoleLevel, bucketByWeek, buildCustomFieldMappingKey, buildSystemFieldMappingKey, canonicalTimeZone, conditionValueKind, consentDayKey, consentEndDay, consentEndingSoonCutoffDay, consentExpiry, createAbsenceSchema, createAbsenceTypeSchema, createAccessGrantSchema, createAccessTypeSchema, createAssetSchema, createAssetTypeSchema, createDepartmentSchema, createEmployeeProfileBlockFieldSchema, createEmployeeProfileBlockSchema, createEmployeeSchema, createLocationSchema, createPermissionSchema, createPositionSchema, createPreboardingFormFieldSchema, createPreboardingFormSchema, createPreboardingFormSectionSchema, createProjectSchema, createRoleSchema, createSeniorityLevelSchema, dateStringSchema, defaultConditionDraft, describeEducation, emailSchema, employeeFilterSchema, employeeProfileFieldTypeSchema, employeeProfileSectionSchema, employmentStatusSchema, employmentTypeSchema, formatEducationPeriod, formatMappableFieldLabel, formatVariableToken, googleAuthSchema, grantableBoardRoles, grantsUnscoped, isBoardRole, isImageMime, isReopenDayKey, isReopenDuration, isTimeZoneNameShape, isValidTimeZone, isValidUaIban, monthCutoffClosed, normalizeIban, notificationScheduleSchema, paginationSchema, parseMappingKey, phoneSchema, preboardingFormCompletionConfigSchema, preboardingFormEmailsConfigSchema, preboardingFormFieldKindSchema, preboardingFormStatusSchema, preboardingFormTypeSchema, preboardingFormWelcomeConfigSchema, projectQuerySchema, projectSourceSchema, projectStatusSchema, projectSyncSettingsSchema, publicPreboardingFormAccessSchema, readableZone, refreshTokenSchema, renameEmployeeProfileBlockSchema, reopenActivityLine, reopenDayKey, reopenDurationLabel, reopenFromBounds, reopenUntil, reopenWindowCoversDay, reopenWindowLive, reorderEmployeeProfileBlockFieldsSchema, reorderEmployeeProfileBlocksSchema, reorderPreboardingFormFieldsSchema, reorderPreboardingFormSectionsSchema, resolvePreboardingConflictSchema, rosterMonthSchema, runningMonthStart, setEmployeeProfileFieldValueSchema, sharePercents, sortOrderSchema, sortSchema, storedZoneOrNull, suggestTemplateForDepartment, summarizeRule, updateAbsenceSchema, updateAbsenceTypeSchema, updateAccessGrantSchema, updateAccessTypeSchema, updateAssetSchema, updateAssetTypeSchema, updateDepartmentSchema, updateEmployeeProfileBlockFieldSchema, updateEmployeeSchema, updateLocationSchema, updateNotificationScheduleSchema, updatePermissionSchema, updatePositionSchema, updatePreboardingFormFieldSchema, updatePreboardingFormSchema, updatePreboardingFormSectionSchema, updateProjectSchema, updateRoleSchema, updateSeniorityLevelSchema, upsertRosterSchema, userRoleSchema, uuidSchema, wallDigitsIn };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map